import './GlobalSearchInput.scss';
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import Search from '@srm/ui/search';
import { RouteNames } from '@/router/router.types';
import { useSearchParams } from 'react-router';
import { useUi } from '@srm/ui/provider';
import { useStableHandlers } from '@srm/ui/hooks/useStableHandlers';
import { useAnalytics } from '@/analytics';
import { isSearchScope, SearchMode, SearchScope } from '@models/search/search.types';
import { buildSearchParams } from '@pages/globalSearch/searchRoute';
import { DEFAULT_PAGE_SIZE } from '@/store/reducers/SearchReducer';
import GlobalSearchOverlay from './GlobalSearchOverlay';
import { subscribeGlobalSearchOpen } from './globalSearchOverlayBus';

const MemoGlobalSearchOverlay = memo(GlobalSearchOverlay);

interface IGlobalSearchInputProps {
    /** Включить/отключить функцию истории поиска */
    enableSearchHistory?: boolean;
}

// Строка поиска подписана на адрес (текст, область и режим живут в нём) и перерисовывается на каждом переходе;
// поле и закрытая панель — только когда меняется то, что они показывают (обработчики стабильны, anchorRef — ref)
const GlobalSearchInput: React.FC<IGlobalSearchInputProps> = ({ enableSearchHistory = true }) => {
    const { analytics } = useAnalytics();

    const { navigate } = useUi();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchText = searchParams.get('text') ?? '';
    const currentScope = searchParams.get('tab');
    const isAiMode = searchParams.get('mode') === 'ai';

    const [isOverlayOpen, setIsOverlayOpen] = useState(false);
    // Держим строку поиска развёрнутой до размонтирования портала: иначе она
    // начинает сужаться под ещё закрывающейся панелью и создаёт второй рывок.
    const [isOverlayMounted, setIsOverlayMounted] = useState(false);
    const anchorRef = useRef<HTMLDivElement | null>(null);

    const openOverlay = useCallback(() => {
        setIsOverlayMounted(true);
        setIsOverlayOpen(true);

        analytics.trackEvent({
            category: 'GlobalSearch',
            action: 'Открытие панели глобального поиска',
            name: 'global-search-input--focus',
        });
    }, [analytics]);

    const closeOverlay = useCallback(() => {
        setIsOverlayOpen(false);
    }, []);

    // «Изменить запрос» со страницы результатов открывает ту же панель.
    useEffect(() => subscribeGlobalSearchOpen(openOverlay), [openOverlay]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                openOverlay();
            }
        };
        document.addEventListener('keydown', onKeyDown);
        return () => document.removeEventListener('keydown', onKeyDown);
    }, [openOverlay]);

    /**
     * Поиск — это переход по адресу. Endpoint выбирает страница результатов по URL:
     * раньше здесь безусловно запускался поиск документов, из-за чего выбранная в панели
     * область подменялась документами.
     */
    const doSearchByString = useCallback(
        (searchString: string, scope: SearchScope, mode: SearchMode) => {
            if (searchString?.length) {
                analytics.trackEvent({
                    category: 'GlobalSearch',
                    action: 'Выполнение глобального поиска',
                    name: 'global-search--search',
                    customDimensions: [{ id: 201, value: searchString }],
                });
                analytics.deleteCustomDimensions([201]);
            }

            const params = buildSearchParams({
                text: searchString,
                scope,
                mode,
                pageIndex: 0,
                pageSize: DEFAULT_PAGE_SIZE,
            });

            void navigate(`${RouteNames.SEARCH}?${params.toString()}`);
        },
        [navigate, analytics],
    );

    const onOpenAdvanced = useCallback(() => {
        void navigate(RouteNames.SEARCH);
    }, [navigate]);

    // setSearchParams и searchParams новые на каждом переходе: обработчики поля — постоянные
    const { onClear, onFocus } = useStableHandlers({
        onClear: () => {
            if (!searchParams.has('text')) {
                return;
            }
            setSearchParams(
                (prev) => {
                    const next = new URLSearchParams(prev);
                    next.delete('text');
                    return next;
                },
                { replace: true },
            );
        },
        onFocus: () => {
            if (!isOverlayOpen) {
                openOverlay();
            }
        },
    });

    const onOverlayClosed = useCallback(() => setIsOverlayMounted(false), []);
    const initialScope = isSearchScope(currentScope) ? currentScope : 'docs';

    const searchField = useMemo(
        () => (
            <Search
                variant="header"
                tone={isAiMode ? 'ai' : undefined}
                aria-label="Поиск"
                onClear={onClear}
                onFocus={onFocus}
                value={searchText}
                onSearchIconClick={openOverlay}
            />
        ),
        [isAiMode, onClear, onFocus, searchText, openOverlay],
    );

    return (
        <div
            className={clsx(
                'global-search',
                isOverlayMounted && 'global-search--open',
                isAiMode && 'global-search--ai',
            )}
            // слот поиска шапки раскрывается на всю ширину, пока панель не размонтирована
            data-search-expanded={isOverlayMounted}
            ref={anchorRef}
        >
            {searchField}

            <MemoGlobalSearchOverlay
                anchorRef={anchorRef}
                opened={isOverlayOpen}
                initialText={searchText}
                initialScope={initialScope}
                initialMode={isAiMode ? 'ai' : 'exact'}
                enableSearchHistory={enableSearchHistory}
                onClose={closeOverlay}
                onClosed={onOverlayClosed}
                onSearch={doSearchByString}
                onOpenAdvanced={onOpenAdvanced}
            />
        </div>
    );
};

export default GlobalSearchInput;
